import React, { useEffect, useState } from "react";
import MonthlyLogConsistency from "./MonthlyLogConsistency";
import MonthlyStreakCard from "./MonthlyStreakCard";
import MonthlyCalorieComparison from "./MonthlyCalorieComparison";
import MonthlyDailyStepsChart from "./MonthlyDailyStepsChart";
import MonthlyActivityTrendChart from "./MonthlyActivityTrendChart";
import MonthlyBloodPressureChart from "./MonthlyBloodPressureChart";
import MonthlyMealSkipTrends from "./MonthlyMealSkipTrends";
import { getInsightsMonthlyData } from "../../../../../services/clientDetails";

// ── API response types ────────────────────────────────────────────────────
interface DayStatus {
  date: string;
  status: string; // "complete" | "partial" | "missed" | "future"
}

interface MonthlyInsightsData {
  period_start: string;
  period_end: string;
  previous_period_start: string;
  previous_period_end: string;
  total_days: number;
  log_consistency: {
    overall: number;
    meals: number;
    water: number;
    sleep: number;
    gut: number;
    last_month_overall: number;
    change_points: number;
  };
  monthly_streak: {
    current_streak: number;
    longest_streak: number;
    days_this_month: number;
    days: DayStatus[];
  };
  calorie_comparison: {
    daily: { date: string; calories_intake: number; calories_burned: number }[];
    this_month_total_intake: number;
    last_month_total_intake: number;
    change_percent: number | null;
  };
  daily_steps: {
    daily: { date: string; steps: number; calories_burned: number; distance_km: number }[];
    total_steps: number;
  };
  activity_trend: {
    daily: { date: string; minutes: number }[];
    avg_duration_minutes: number;
  };
  blood_pressure: { date: string; systolic: number | null; diastolic: number | null }[];
  meal_skip_trend: {
    daily: { date: string; skipped_count: number; skipped_meals: string[] }[];
    avg_skips_per_day: number;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────
const formatDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const dayOfMonth = (dateStr: string): number => {
  const parts = dateStr.split("-");
  return parseInt(parts[2], 10);
};

const mapStreakStatus = (
  val: string,
): "none" | "partial" | "logged" | "upcoming" => {
  if (val === "complete") return "logged";
  if (val === "partial") return "partial";
  if (val === "future") return "upcoming";
  return "none";
};

interface MonthlyInsightsViewProps {
  userId: number;
  monthStart: Date;
  refreshKey?: number;
  onLoadingChange?: (loading: boolean) => void;
}

const MonthlyInsightsView: React.FC<MonthlyInsightsViewProps> = ({
  userId,
  monthStart,
  refreshKey = 0,
  onLoadingChange,
}) => {
  const [data, setData] = useState<MonthlyInsightsData | null>(null);

  const daysInMonth = new Date(
    monthStart.getFullYear(),
    monthStart.getMonth() + 1,
    0,
  ).getDate();

  const monthKey = monthStart.toISOString();

  useEffect(() => {
    const fetchInsights = async () => {
      setData(null);
      onLoadingChange?.(true);
      try {
        const json: MonthlyInsightsData = await getInsightsMonthlyData(
          userId,
          formatDate(monthStart),
        );
        setData(json);
      } catch (err) {
        console.error("MonthlyInsightsView fetch error:", err);
        setData(null);
      } finally {
        onLoadingChange?.(false);
      }
    };
    fetchInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, monthStart, refreshKey]);

  // ── Derived per-chart data ─────────────────────────────────────────────
  const lc = data?.log_consistency;
  const ms = data?.monthly_streak;
  const cc = data?.calorie_comparison;
  const ds = data?.daily_steps;
  const at = data?.activity_trend;
  const bp = data?.blood_pressure;
  const skip = data?.meal_skip_trend;

  const streakDayLogs = ms
    ? (ms.days.map((d) => mapStreakStatus(d.status)) as (
        | "none"
        | "partial"
        | "logged"
        | "upcoming"
      )[])
    : undefined;

  const daysMissed = ms
    ? ms.days.filter((d) => d.status === "missed").length
    : undefined;

  const calorieData = cc?.daily.map((d) => ({
    day: dayOfMonth(d.date),
    intake: d.calories_intake,
    burned: d.calories_burned,
  }));

  const stepsData = ds?.daily.map((d) => ({
    day: dayOfMonth(d.date),
    steps: d.steps,
  }));

  const activityData = at?.daily.map((d) => ({
    day: dayOfMonth(d.date),
    minutes: d.minutes,
  }));

  const bpData = bp?.map((d) => ({
    day: dayOfMonth(d.date),
    systolic: d.systolic,
    diastolic: d.diastolic,
  }));

  const skipData = skip?.daily.map((d) => ({
    day: dayOfMonth(d.date),
    skips: d.skipped_count,
  }));

  const avgDurationLabel =
    at?.avg_duration_minutes != null
      ? at.avg_duration_minutes >= 60
        ? `${(at.avg_duration_minutes / 60).toFixed(1)} hour`
        : `${Math.round(at.avg_duration_minutes)} min`
      : undefined;

  return (
    <>
      {/* Row 1: Log Consistency | Streak (30-day grid) */}
      <div
        key={`m-r1-${refreshKey}-${monthKey}`}
        className="grid grid-cols-9 gap-4 px-6 mb-4 items-stretch"
      >
        <div className="col-span-3 h-full">
          <MonthlyLogConsistency
            consistency={lc?.overall}
            lastMonthConsistency={lc?.last_month_overall}
            meals={lc?.meals}
            water={lc?.water}
            sleep={lc?.sleep}
            gut={lc?.gut}
          />
        </div>
        <div className="col-span-6 h-full">
          <MonthlyStreakCard
            currentStreak={ms?.current_streak}
            longestStreak={ms?.longest_streak}
            daysMissed={daysMissed}
            monthStart={monthStart}
            dayLogs={streakDayLogs}
          />
        </div>
      </div>

      {/* Row 2: Calorie Comparison */}
      <div key={`m-r2-${refreshKey}-${monthKey}`} className="px-6 mb-4">
        <MonthlyCalorieComparison data={calorieData} daysInMonth={daysInMonth} />
      </div>

      {/* Row 3: Daily Steps */}
      <div key={`m-r3-${refreshKey}-${monthKey}`} className="px-6 mb-4">
        <MonthlyDailyStepsChart data={stepsData} daysInMonth={daysInMonth} />
      </div>

      {/* Row 4: Activity Trend */}
      <div key={`m-r4-${refreshKey}-${monthKey}`} className="px-6 mb-4">
        <MonthlyActivityTrendChart
          data={activityData}
          daysInMonth={daysInMonth}
          avgLabel={avgDurationLabel}
        />
      </div>

      {/* Row 5: Blood Pressure */}
      <div key={`m-r5-${refreshKey}-${monthKey}`} className="px-6 mb-4">
        <MonthlyBloodPressureChart data={bpData} daysInMonth={daysInMonth} />
      </div>

      {/* Row 6: Meal Skip Trends */}
      <div key={`m-r6-${refreshKey}-${monthKey}`} className="px-6 mb-4">
        <MonthlyMealSkipTrends
          data={skipData}
          daysInMonth={daysInMonth}
          avgSkipsPerDay={skip?.avg_skips_per_day}
        />
      </div>
    </>
  );
};

export default MonthlyInsightsView;
